'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { FunctionComponent, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from '../Button/Button'
import { useMutation } from '../../../core/hook/useMutation'

import { ApolloError } from '@apollo/client'
import { useTranslation } from '../../context/ConfigContext'
import Icon from '../Icon/Icon'
import { Icons } from '../Icon/constants'
import { handleError } from '../../../core/hook/utils'
import { SubscribeToNewsletterDocument } from '../../../queries/SubscribeToNewsletter.gql'
import { Civility } from '../Form/Civility'
import { setCookie } from 'cookies-next'
import { COOKIE_NEWSLETTER_USER_ALREADY_SUBSCRIBED } from '../Cookie/CookieNewsletter'
import { gtmPush } from '../../utils/gtmHelpers'

const SubscribeToNewsletter: FunctionComponent = () => {
  const t = useTranslation()

  const [subscribeToNewsletter] = useMutation(SubscribeToNewsletterDocument)
  const [globalError, setGlobalError] = useState<string | null>()
  const [successMessage, setSuccessMessage] = useState<string | null>()

  const NewsletterForm = z.object({
    civility: z
      .string({ required_error: t('This is a required field.', {}, false) })
      .min(1, { message: t('This is a required field.', {}, false) }),
    firstname: z
      .string({ required_error: t('This is a required field.', {}, false) })
      .min(1, { message: t('This is a required field.', {}, false) })
      .min(2, { message: t('%1 minimum characters', { variables: { 0: 2 } }, false) })
      .max(55, {
        message: t('%1 maximum characters', { variables: { 0: 55 } }, false),
      }),
    lastname: z
      .string({ required_error: t('This is a required field.', {}, false) })
      .min(1, { message: t('This is a required field.', {}, false) })
      .min(2, { message: t('%1 minimum characters', { variables: { 0: 2 } }, false) })
      .max(55, {
        message: t('%1 maximum characters', { variables: { 0: 55 } }, false),
      }),
    email: z
      .string({ required_error: t('This is a required field.', {}, false) })
      .min(1, { message: t('This is a required field.', {}, false) })
      .email({
        message: t('Please enter a valid email address (Ex: johndoe@domain.com).', {}, false),
      }),
    birth: z
      .string({ required_error: t('This is a required field.', {}, false) })
      .min(1, { message: t('This is a required field.', {}, false) }),
    confirm: z
      .boolean()
      .refine((confirmData) => confirmData, t('This is a required field.', {}, false)),
  })

  type NewsletterFormType = z.infer<typeof NewsletterForm>

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Contact>({
    resolver: zodResolver(NewsletterForm),
  })

  const onSubmit = (data: NewsletterFormType) => {
    setGlobalError(null)

    subscribeToNewsletter({
      variables: {
        input: {
          email: data.email,
          firstname: data.firstname,
          lastname: data.lastname,
          prefix: data.civility,
          dob: data.birth ? new Date(data.birth).getTime() / 1000 : undefined,
        },
      },
    })
      .then((subscribeToNewsletterResponse) => {
        window.scrollTo({ top: 0 })
        setCookie(COOKIE_NEWSLETTER_USER_ALREADY_SUBSCRIBED, '1')
        switch (subscribeToNewsletterResponse.data?.subscribeToNewsletter?.status) {
          case 'SUBSCRIBED':
            setSuccessMessage(
              `${data.email} : ${t('Your subscription has been confirmed.', {}, false)}`,
            )
            gtmPush({
              event: 'subscribe_newsletter',
            })
            break
          case 'NOT_ACTIVE':
            setSuccessMessage(
              `${data.email} : ${t('newsletter-text-email-confirmation', {}, false)}`,
            )
            break
          case 'UNCONFIRMED':
            setSuccessMessage(`${data.email} : ${t('newsletter-unconfirmed-text', {}, false)}`)
            break
        }
      })
      .catch((e: ApolloError) => {
        handleError(e, false)
        setGlobalError(e.message)
      })
  }

  return (
    <>
      {!successMessage ? (
        <form onSubmit={handleSubmit(onSubmit)} data-test='subscribe-to-newsletter-form'>
          <div className='form-control'>
            <label htmlFor='email'>{t('Email', {}, true)}*</label>
            <input type='email' {...register('email')} placeholder={t('Email', {}, true)} />
            {errors.email?.message && <p className='error-message'>{errors.email.message}</p>}
          </div>

          <div className='form-control'>
            <label htmlFor='civility'>
              {t('product_alert_popup_civility_placeholder', {}, true)}*
            </label>
            <Civility register={register} name='civility' />
            {errors.civility?.message && <p className='error-message'>{errors.civility.message}</p>}
          </div>

          <div className='form-control'>
            <label htmlFor='firstname'>{t('Firstname', {}, true)}*</label>
            <input type='text' {...register('firstname')} placeholder={t('Firstname', {}, false)} />
            {errors.firstname?.message && (
              <p className='error-message'>{errors.firstname.message}</p>
            )}
          </div>

          <div className='form-control'>
            <label htmlFor='lastname'>{t('Lastname', {}, true).concat('*')}</label>
            <input type='text' {...register('lastname')} placeholder={t('Lastname', {}, false)} />
            {errors.lastname?.message && <p className='error-message'>{errors.lastname.message}</p>}
          </div>

          <div className='form-control date'>
            <label htmlFor='birth'>{t('Date of Birth', {}, true)}*</label>
            <input type='date' id='birth' {...register('birth')} />
            {errors.birth?.message && <p className='error-message'>{errors.birth.message}</p>}
          </div>

          <div className='form-control checkbox'>
            <input type='checkbox' id='confirm' {...register('confirm')} />
            <label htmlFor='confirm'>{t('newsletter-text-confirmation', {}, true)}</label>
          </div>
          {errors.confirm?.message && <p className='error-message'>{errors.confirm.message}</p>}
          {globalError && <p className='error-message'>{globalError}</p>}
          <Button
            classname='black'
            type='submit'
            size='large'
            label={t('Subscribe', {}, false)}
            style='dark'
          />
        </form>
      ) : (
        <div className='newsletter-success'>
          <Icon iconName={Icons.SUCCESS} />
          <p>{successMessage}</p>
        </div>
      )}
    </>
  )
}

export default SubscribeToNewsletter
